import styled from 'styled-components';
import { darken, rgba, transparentize } from 'polished';
import { COLORS, LAYOUT } from '@/constants/styles';
import { StyledSelectContainer } from '@/components/uielements/selectInput/styles';
import { StyledText } from '@/components/checkout/uielements/styles';
import { ExclamationCircle } from '@/components/uielements/icon/exclamation-circle';
import { getFontStyles } from '@/redux/cmsConfig/utils';

export const StyledNoAvailability = styled.p`
  font-size: 12px;
  font-style: italic;
  line-height: normal;
  margin: 0 0 ${LAYOUT.GUTTER}px;
`;

interface StyledTimeSelectProps {
  $showLoading: boolean;
  $showError: boolean;
}

export const StyledTimeSelect = styled.div<StyledTimeSelectProps>`
  position: relative;
  transition: opacity 0.2s linear;
  ${({ $showLoading }) =>
    $showLoading &&
    `
opacity: 0.5;
`}
  .select-style {
    ${({ $showError }) =>
      $showError &&
      `
  border-color: red
`}
  }
`;

interface StyledLoaderProps {
  $right?: number;
  $top?: number;
}

export const StyledLoader = styled.div<StyledLoaderProps>`
  height: 22px;
  position: absolute;
  right: ${({ $right }) => $right ?? 35}px;
  top: ${({ $top }) => $top ?? 14}px;
  width: 22px;
  /* Spinner Circle Rotation */
  border: 3px rgba(0, 0, 0, 0.25) solid;
  border-top: 3px rgba(0, 0, 0, 1) solid;
  border-radius: 50%;
  -webkit-animation: spCircRot 0.6s infinite linear;
  animation: spCircRot 0.6s infinite linear;
  @-webkit-keyframes spCircRot {
    from {
      -webkit-transform: rotate(0deg);
    }
    to {
      -webkit-transform: rotate(359deg);
    }
  }
  @keyframes spCircRot {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
`;

export const StyledDayTimeSelectContainer = styled.div<{ $stack: boolean }>`
  align-items: center;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  @media (max-width: 767px) {
    display: ${({ $stack = true }) => ($stack ? 'block' : 'flex')};
    ${({ $stack = true }) =>
      $stack &&
      `
        > div + div { margin-top: 10px; }
      `}
  }
  > div {
    flex: 1;
  }
`;

export const StyledItalicSelectContainer = styled(StyledSelectContainer)`
  position: relative;

  .select-style {
    transition: outline 0.2s ease;

    select {
      font-style: italic;
    }

    /* Focus styles using outline for better accessibility */
    &:has(select:focus) {
      outline: 3px solid ${({ theme }) => theme.global.primary_active_color || '#000'};
      outline-offset: 2px;
    }

    &:has(select:focus:not(:focus-visible)) {
      outline: none;
    }

    &:has(select:focus-visible) {
      outline: 3px solid ${({ theme }) => theme.global.primary_active_color || '#000'};
      outline-offset: 2px;
    }
  }
`;

export const StyledTimePicker = styled.span`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledTimePickerContainer = styled(StyledDayTimeSelectContainer)<{
  $stack?: boolean;
}>`
  min-height: 90px;
  align-items: flex-end;
  gap: ${LAYOUT.GUTTER}px;
  @media (max-width: 767px) {
    gap: ${({ $stack = true }) => ($stack ? 0 : `${LAYOUT.GUTTER}px`)};
  }
`;

export const StyledTimePickerLabel = styled(StyledText)`
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  font-style: italic;
  text-align: left;
`;

// TEAM-14386: pending (uncommitted) selection treatment. Colours follow the
// established warning notice used elsewhere in checkout (see
// features/checkout/summary/items/items-unavailable-notice.tsx) so this modal does
// not introduce a second warning pattern of its own.
export const StyledChangedMarker = styled.span`
  margin-left: 6px;
  color: ${COLORS.DARKORANGE};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

export const StyledPendingChangeWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 12px;
  padding: 10px 12px;
  border: 1px solid ${COLORS.PRIMARY_WARNING};
  background-color: ${rgba(COLORS.PRIMARY_WARNING, 0.1)};
  border-radius: 8px;
  text-align: left;
`;

export const StyledPendingChangeWarningIcon = styled(ExclamationCircle)`
  color: ${COLORS.PRIMARY_WARNING};
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 1px;
`;

export const StyledPendingChangeWarningText = styled.span`
  color: ${COLORS.DARKORANGE};
  font-size: 12px;
  line-height: 19.5px;
`;

export const StyledRadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const StyledRadioOption = styled.label<{ checked: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid
    ${({ checked, theme }) =>
      checked
        ? theme.global.primary_active_color || '#000'
        : theme.global.primary_border_color || '#ddd'};
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  background-color: ${({ checked, theme }) => {
    return checked ? transparentize(0.95, theme.global.primary_active_color) : 'inherit';
  }};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    border-color: ${({ disabled, theme }) =>
      disabled ? '#e5e5e5' : theme.global.primary_active_color || '#000'};
  }
`;

export const StyledRadioInput = styled.input`
  all: unset;
  border: 2px solid;
  border-color: ${({ theme }) => theme.global.primary_border_color ?? '#ddd'};
  box-sizing: border-box;
  border-radius: 100%;
  height: 1.25rem;
  width: 1.25rem;
  min-width: 1.25rem;
  position: relative;
  cursor: pointer;

  /* Focus styles */
  &:focus {
    outline: 3px solid ${({ theme }) => theme.global.primary_active_color || 'inherit'};
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.global.primary_active_color || 'inherit'};
    outline-offset: 2px;
  }

  &:checked {
    border-color: ${({ theme }) => theme.global.primary_active_color ?? 'inherit'};
  }

  &:checked:after {
    background-color: ${({ theme }) => theme.global.primary_active_color ?? 'inherit'};
    border-radius: 100%;
    content: '';
    position: absolute;
    inset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const StyledRadioContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  color: ${({ theme }) => getFontStyles(theme.checkout.secondary_font).color};
  text-align: left;
`;

export const StyledRadioLabel = styled.span`
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.4;
`;

export const StyledRadioSubtitle = styled.span`
  font-size: 0.875rem;
  opacity: 0.7;
  line-height: 1.4;
`;

export const StyledTimepickerSection = styled.div<{ show: boolean }>`
  display: ${({ show }) => (show ? 'block' : 'none')};
  margin-top: 0.5rem;
`;

export const StyledUnavailableBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  margin-left: 0.5rem;
  background-color: ${({ theme }) => darken(0.1, theme.checkout?.background_color || '#f5f5f5')};
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 12px;
  line-height: 1;
`;

export const StyledFieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  min-inline-size: auto;
`;

export const StyledLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const StyledScheduledUnavailableMessage = styled(StyledRadioSubtitle)`
  border-top: 1px solid ${({ theme }) => theme.global.primary_border_color};
  margin-top: 0.5rem;
  padding-top: 0.75rem;
`;

// TEAM-14275: the settled "this day has nothing bookable" state. Subdued
// secondary-font treatment, matching StyledOrderingUnavailableSubtitle below,
// rather than the orange pending-change warning — nothing has gone wrong, the
// diner just needs to pick a different date. Kept as its own declaration rather
// than extending that one, which carries a desktop font-size bump this message
// does not want.
export const StyledNoTimesMessage = styled.span`
  color: ${({ theme }) => getFontStyles(theme.checkout.secondary_font).color};
  display: block;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
`;

export const StyledOrderingUnavailable = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  text-align: center;
`;

export const StyledOrderingUnavailableIcon = styled(ExclamationCircle)`
  color: ${({ theme }) => theme.global.primary_active_color};
  height: 56px;
  width: 56px;
  margin-bottom: 12px;

  @media (min-width: 640px) {
    margin-bottom: 20px;
  }
`;

export const StyledOrderingUnavailableTitle = styled.span`
  color: ${({ theme }) => getFontStyles(theme.checkout.secondary_font).color};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 12px;

  @media (min-width: 640px) {
    font-size: 18px;
  }
`;

export const StyledOrderingUnavailableSubtitle = styled.span`
  color: ${({ theme }) => getFontStyles(theme.checkout.secondary_font).color};
  font-size: 14px;
  line-height: 1.4;

  @media (min-width: 640px) {
    font-size: 15px;
  }
`;

export const StyledOrderingUnavailablePrimaryText = styled.a`
  color: ${({ theme }) => theme.global.primary_active_color};
`;
